import { InputNumber, Radio, RadioChangeEvent, Space } from 'antd';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { SeriesOptionsType } from 'highcharts';
import { useMemo, useState } from 'react';
import { LiaoChartData } from '../types';
import { MAX_CURVE_POINTS, MIN_CURVE_POINTS } from '../../../../utils/curveGeneration';
import { Chart } from '../../../../chart/BaseChart';
import { ChartSpec } from '../../../../chart/chartSpec';

type LiaoChartProps = {
  chartData: LiaoChartData;
  showLambda: boolean;
  numberOfPoints: number;
  setNumberOfPoints: (n: number) => void;
};

export function LiaoChart({ chartData, showLambda, numberOfPoints, setNumberOfPoints }: LiaoChartProps) {
  const { isDay } = useTheme();
  const colors = useMemo(() => getColors(isDay), [isDay]);

  const [isLogarithmic, setIsLogarithmic] = useState<boolean>(false);

  const spec: ChartSpec = useMemo(() => {
    const minX = chartData.original.reduce(
      (acc, cur) => {
        if (!acc) {
          return cur.x;
        }

        if (cur.x < acc) {
          return cur.x;
        }

        return acc;
      },
      undefined as undefined | number
    );

    const maxX = chartData.original.reduce(
      (acc, cur) => {
        if (!acc) {
          return cur.x;
        }

        if (cur.x > acc) {
          return cur.x;
        }

        return acc;
      },
      undefined as undefined | number
    );

    const minY = chartData.original.reduce(
      (acc, cur) => {
        if (!acc) {
          return cur.y;
        }

        if (cur.y < acc) {
          return cur.y;
        }

        return acc;
      },
      undefined as undefined | number
    );

    const maxY = chartData.original.reduce(
      (acc, cur) => {
        if (!acc) {
          return cur.y;
        }

        if (cur.y > acc) {
          return cur.y;
        }

        return acc;
      },
      undefined as undefined | number
    );

    const series: SeriesOptionsType[] = [
      {
        name: 'Data SCAL',
        type: 'scatter',
        data: chartData.original,
        color: '#f47936',
        marker: {
          radius: 5
        },
        zIndex: 2
      },
      {
        name: 'Pc Liao',
        type: 'spline',
        data: chartData.calculated,
        color: '#72E2D8',
        marker: {
          enabled: false
        },
        lineWidth: 2,
        enableMouseTracking: false,
        states: {
          hover: {
            enabled: false
          }
        },
        dataLabels: {
          style: {
            color: '#72E2D8'
          }
        },
        zIndex: 0
      }
    ];

    if (showLambda) {
      series.push({
        name: 'Pc Brooks-Corey',
        type: 'spline',
        data: chartData.lambda,
        color: '#F5C242',
        marker: {
          enabled: false
        },
        lineWidth: 2,
        enableMouseTracking: false,
        dataLabels: {
          style: {
            color: '#4A90E2'
          }
        }
      });
    }

    return {
      height: '50%',
      title: isLogarithmic ? 'Logarithm Pc x Sw' : 'Pc x Sw',
      xAxis: {
        title: isLogarithmic ? 'Log Sw' : 'Water Saturation',
        type: isLogarithmic ? 'logarithmic' : 'linear',
        max: isLogarithmic ? 1 : maxX,
        min: isLogarithmic ? 0.001 : minX
      },
      yAxis: {
        title: isLogarithmic ? 'Log Pc' : 'Capillary Pressure',
        type: isLogarithmic ? 'logarithmic' : 'linear',
        max: isLogarithmic ? 1000 : maxY,
        min: isLogarithmic ? 0.1 : minY
      },
      series: series,
      plotOptions: {
        scatter: {
          marker: {
            enabled: true
          }
        }
      }
    };
  }, [isLogarithmic, chartData, showLambda, colors.text]);

  const handleChartTypeChange = (event: RadioChangeEvent) => {
    setIsLogarithmic(event.target.value);
  };

  return (
    <div style={{ marginLeft: '0.5rem' }}>
      <Space
        direction="vertical"
        style={{
          backgroundColor: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          transition: 'width 0.5s',
          width: '100%'
        }}
      >
        <div
          style={{
            display: 'flex',
            padding: '0.75rem',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Radio.Group
            onChange={e => {
              handleChartTypeChange(e);
            }}
            options={[
              { label: 'Linear', value: false },
              { label: 'Logarithmic', value: true }
            ]}
            defaultValue={false}
            style={{ backgroundColor: 'transparent !important' }}
            block
            optionType="button"
            buttonStyle="solid"
            className={isDay ? 'day-radio' : 'night-radio'}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '12px', color: colors.text }}>Curve points:</span>
            <InputNumber
              min={MIN_CURVE_POINTS}
              max={MAX_CURVE_POINTS}
              step={10}
              value={numberOfPoints}
              onChange={val => { if (val != null) setNumberOfPoints(val); }}
              size="small"
              style={{ width: '70px' }}
            />
          </div>
        </div>
        <Space style={{ display: 'block' }}>
          <div style={{ padding: '0.75rem' }}>
            <Chart spec={spec} />
          </div>
        </Space>
      </Space>
    </div>
  );
}
